import { ChangeEvent, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Field } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import TextField from '@atlaskit/textfield';

import { emailIsValid } from '@/components/signup/utils/email-is-valid';
import { fontSize } from '@/components/signup/utils/font-size';
import { wacToken } from '@/libraries/design/foundations';

import { useAccountSigninContext } from '../hooks/account-signin-context';

const HELP_TEXT_ID = 'account_signin_email_help_text';

export const Input = ({
	helpText,
	errorMessageText,
	labelText,
	placeholderText,
}: {
	helpText?: string | null;
	errorMessageText?: string;
	labelText?: string;
	placeholderText?: string;
}) => {
	const {
		isReadyForInvalid,
		setIsEmailValid,
		setIsReadyForInvalid,
		setTypeAheadEmail,
		typeAheadEmail,
	} = useAccountSigninContext();
	const [email, setEmail] = useState(typeAheadEmail);
	const [isValid, setValid] = useState<boolean>();
	const intl = useIntl();

	if (helpText === undefined) {
		helpText = intl.formatMessage({
			id: 'gwp.accountSignin.helperText',
			defaultMessage: 'Using a work email helps find teammates and boost collaboration.',
		});
	}
	if (errorMessageText === undefined) {
		errorMessageText = intl.formatMessage({
			id: 'gwp.accountSignin.invalidEmail',
			defaultMessage: 'Please enter a valid email address.',
		});
	}
	if (labelText === undefined) {
		labelText = intl.formatMessage({
			id: 'gwp.accountSignin.workEmail',
			defaultMessage: 'Work email',
		});
	}
	if (placeholderText === undefined) {
		placeholderText = intl.formatMessage({
			id: 'gwp.accountSignin.emailPlaceholder',
			defaultMessage: 'you@company.com',
		});
	}

	useEffect(() => {
		const isValid = emailIsValid(email);

		// wait to show invalid state if the user is not finished typing
		if (!isValid && !isReadyForInvalid) {
			setValid(undefined);
			return;
		}

		setValid(isValid);
		setIsEmailValid(isValid);
	}, [email, setIsEmailValid, isReadyForInvalid]);

	return (
		<div css={fieldCss}>
			<Field aria-required={true} isRequired label={labelText} name="email">
				{({ fieldProps }) => (
					<div
						css={[
							inputCss,
							isValid === true && inputValidCss,
							isValid === false && inputInvalidCss,
						]}
					>
						<TextField
							{...fieldProps}
							autoComplete="on"
							aria-describedby={HELP_TEXT_ID}
							elemAfterInput={<InputIcon valid={isValid} />}
							onBlur={() => setIsReadyForInvalid(true)}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								setEmail(e.target.value);
								setTypeAheadEmail(e.target.value);
								fieldProps.onChange(e);
							}}
							placeholder={placeholderText}
							testId="email_field"
							type="email"
							value={email}
						/>
						{isValid === false ? (
							<div css={errorMessageCss} id={HELP_TEXT_ID} role="alert">
								{errorMessageText}
							</div>
						) : helpText ? (
							<div css={helpTextCss} id={HELP_TEXT_ID}>
								{helpText}
							</div>
						) : null}
					</div>
				)}
			</Field>
		</div>
	);
};

export const InputIcon = ({ valid }: { valid: boolean | undefined }) => {
	const { formatMessage } = useIntl();

	const validMessage = formatMessage({
		id: 'gwp.accountSignin.validEmailIcon',
		defaultMessage: 'Email is valid',
	});

	const invalidMessage = formatMessage({
		id: 'gwp.accountSignin.invalidEmailIcon',
		defaultMessage: 'Email is invalid',
	});

	if (valid === undefined) {
		return null;
	}

	return valid ? (
		<div css={iconValidCss}>
			<SuccessIcon label={validMessage} />
		</div>
	) : (
		<div css={iconInvalidCss}>
			<ErrorIcon label={invalidMessage} />
		</div>
	);
};

const green600 = '#22A06B';
const red700 = '#C9372C';
const red600 = '#E2483D';

const errorMessageCss = css({
	color: red700,
	fontSize: fontSize.xs,
	lineHeight: 1.5,
	marginBlockStart: 6,
});

const helpTextCss = css({
	color: wacToken('color.neutral.dark500'),
	fontSize: fontSize.xs,
	lineHeight: 1.5,
	marginBlockStart: 6,
});

const fieldCss = css({
	marginBlockEnd: 16,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		fontFamily: wacToken('font.charlie.text'),
		fontSize: fontSize.md,
		paddingInlineStart: '12px !important',
	},
	['&:first-of-type']: {
		marginBlockStart: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	label: {
		color: 'inherit',
		fontFamily: wacToken('font.charlie.text'),
		fontWeight: 500,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
		['[aria-hidden=true]']: {
			display: 'none',
		},
	},
});

const iconWrapperStyle = {
	alignItems: 'center',
	display: 'flex',
	fill: wacToken('color.neutral.light0'),
	fontSize: fontSize.md,
	paddingInlineEnd: 8,
};

const iconValidCss = css({
	...iconWrapperStyle,
	color: green600,
});

const iconInvalidCss = css({
	...iconWrapperStyle,
	color: red600,
});

const inputCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['> div']: {
		borderRadius: 21,
	},
});

const inputInvalidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['> div']: {
		borderColor: `${red600} !important`,
	},
});

const inputValidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['> div']: {
		borderColor: `${green600} !important`,
	},
});
